import React, { useRef, useCallback, useMemo, useEffect, useState, Fragment } from "react";
import { useSearchParams } from "react-router-dom";

import COMMON from "common";
import api from "services/api";
import sanitizeObject from "common/sanitize-object";
import serveRequestErrors from "common/serve-request-errors";
import AppEmptyState from "components/app-empty-state";
import AppSearchInput from "components/app-search-input";
import AppTable, { AppTableEditIcon } from "components/app-table";
import addIcon from "assets/images/add-icon.svg";
import filterIcon from "assets/images/components/app-multi-select-input/filter-icon.svg";
import AppCourseModal from "components/pages/e-learning/app-course-modal";
import queryParamsEntries from "common/query-params-entries";
import AppInput from "components/app-input";
import AppButton from "components/app-button";
import { useFormik } from "formik";

const PageCourses = (props) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const memoSetSearchParams = useRef(setSearchParams);
    const searchInputRef = useRef();
    const createCourseRef = useRef();
    const paramsRef = useRef({
        page: parseInt(searchParams.get("page")) || 1,
        sort: searchParams.get("sort") || "",
        title: searchParams.get("title") || "",
    });
    const [data, setData] = useState({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });
    const cancelRequest = useMemo(() => props.onHandleCancelRequest, [props.onHandleCancelRequest]);
    const isEmptyState = useMemo(() => data.status === true, [data.status]);
    const initialValues = useMemo(() => ({ title: "" }), []);

    const formik = useFormik({
        initialValues,
        onSubmit: (values) => {
            onHandleSubmitSearch(values);
        },
    });

    const onHandleSubmitSearch = (values) => {
        searchInputRef.current.onhandleCloseAdvanceSearch();

        paramsRef.current = { ...paramsRef.current, page: 1, ...values };

        onHandleGetList();
    };

    const onHandleGetList = useCallback(async () => {
        let response = null;

        setData({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });

        try {
            const payload = { ...paramsRef.current, size: 10 };

            memoSetSearchParams.current(sanitizeObject(payload), { replace: true });

            payload.page = paramsRef.current.page - 1;

            response = await api.get.course.list(sanitizeObject(payload));
        } catch (error) {
            serveRequestErrors(error);
        }

        if (response) {
            setData((prev) => ({
                ...prev,
                page: paramsRef.current.page,
                prev: !response.first,
                next: !response.last,
                items: response.content,
                total: response.totalPages,
                totalElements: response.totalElements,
                offset: response?.pageable?.offset || 0,
                status: response?.empty,
            }));
        }
    }, []);

    //prettier-ignore
    const onHandlePagination = useCallback((event) => {
        const control = event.currentTarget?.getAttribute("data-ctrl");

        if (control) {
            if (control === "prev") {
                if (paramsRef.current.page <= 1) return;
                paramsRef.current.page -= 1;
            } else {
                if (paramsRef.current.page >= data.total) return;
                paramsRef.current.page += 1;
            }
        } else {
            paramsRef.current.page = event.target.value;
        }

        onHandleGetList();
    }, [onHandleGetList, data.total]);

    //prettier-ignore
    const onHandleResetSearch = () => {
        formik.setValues(formik.initialValues);

        paramsRef.current = { page: 1, sort: "", ...formik.initialValues };

        onHandleGetList();
    };

    //prettier-ignore
    const onHandleSort = useCallback((id, order) => {
        paramsRef.current.sort = order ? id + "," + order : "";

        paramsRef.current.page = 1;

        onHandleGetList();
    }, [onHandleGetList]);

    //prettier-ignore
    const onHandleEdit = useCallback((obj) => {
        createCourseRef.current?.onHandleShow(obj);
    }, []);

    //prettier-ignore
    const onHandleCreate = useCallback(() => {
        createCourseRef.current.onHandleShow();
    }, []);

    //prettier-ignore
    const MenuCell = useCallback(({ row }) => {
        return <AppTableEditIcon onClick={() => onHandleEdit(row.original)} />;
    }, [onHandleEdit]);

    //prettier-ignore
    const columns = useMemo(() => [
        {
            Header: "Title",
            accessor: "title",
            disableSortBy: false,
        },
        {
            Header: "Description",
            accessor: "description",
            disableSortBy: true,
        },
        {
            Header: "",
            accessor: "*",
            disableSortBy: true,
            Cell: MenuCell,
        },
    ], [MenuCell]);

    //prettier-ignore
    const onHandleRemoveField = (field) => {
        formik.setFieldValue(field, "");
        paramsRef.current = { ...paramsRef.current, page: 1, [field]: "" };
        onHandleGetList();
    };

    //prettier-ignore
    const advanceSearchValues = useMemo(() => {
        const params = queryParamsEntries(searchParams);
        let values = { title: params.title };

        return values;
    }, [searchParams]);

    //prettier-ignore
    const onHandleShowAdvanceSearch = (event) => {
        if (searchInputRef.current?.onHandleShowAdvanceSearch) {
            searchInputRef.current.onHandleShowAdvanceSearch(event);
        }
    };

    //prettier-ignore
    const headerButtons = useMemo(() => {
        const buttons = [
            { label: "Filter", icon: filterIcon, outline: true, onClick: onHandleShowAdvanceSearch },
            { label: "Add Course", icon: addIcon, onClick: onHandleCreate },
        ];
        return buttons;
    }, [onHandleCreate]);

    useEffect(() => {
        onHandleGetList();
    }, [onHandleGetList]);

    useEffect(() => {
        return () => {
            cancelRequest(COMMON.ENDPOINT_PATH.COURSE.LIST);
        };
    }, [cancelRequest]);

    return (
        <div className="page-courses">
            <div className="courses">
                <AppSearchInput ref={searchInputRef} multiValues={advanceSearchValues} onRemoveField={onHandleRemoveField} buttons={headerButtons}>
                    <form className="app-advance-search-form" onSubmit={formik.handleSubmit}>
                        <div className="advance-form">
                            <AppInput type="text" name="title" label="Course Title" placeholder="Enter course title" value={formik.values.title} onChange={formik.handleChange} />

                            <div className="advance-form__button-container">
                                <AppButton type="button" label="Clear" outline onClick={onHandleResetSearch} />
                                <AppButton type="submit" label="Search" />
                            </div>
                        </div>
                    </form>
                </AppSearchInput>
                {isEmptyState && <AppEmptyState title="No records found" description="You don’t have any records yet" label="Add New Course" onClick={onHandleCreate} />}

                {!isEmptyState && (
                    <Fragment>
                        {(paramsRef.current.title) && (
                            <div className="courses__results">
                                <p className="courses__text">{data.totalElements} results found</p>
                            </div>
                        )}
                        <AppTable columns={columns} pages={data} onHandlePagination={onHandlePagination} onHandleSort={onHandleSort} />
                    </Fragment>
                )}
            </div>

            <AppCourseModal ref={createCourseRef} onHandleGetList={onHandleGetList} />
        </div>
    );
};

export default PageCourses;